import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const addConsistentStyles = () => {
  if (document.getElementById('reset-password-modal-styles')) return;
  
  const style = document.createElement('style');
  style.id = 'reset-password-modal-styles';
  style.textContent = `
    .font-poppins { font-family: 'Poppins', sans-serif; }
    .font-opensans { font-family: 'Open Sans', sans-serif; }
    .text-primary { color: #1E3A8A; }
    .bg-primary { background-color: #1E3A8A; }
    .text-accent { color: #F97316; }
    .bg-accent { background-color: #F97316; }
    .bg-bg-cream { background-color: #FFF7F0; }
    .text-text-dark { color: #333333; }
    .border-primary { border-color: #1E3A8A; }
    .hover\\:bg-primary:hover { background-color: #1E3A8A; }
    .hover\\:text-primary:hover { color: #1E3A8A; }
    .hover\\:bg-orange-600:hover { background-color: #ea580c; }
    .hover\\:bg-blue-800:hover { background-color: #1e40af; }
  `;
  document.head.appendChild(style);
};

const ResetPasswordPage = ({ onClose, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add consistent styles and fonts
  useEffect(() => {
    addConsistentStyles();
    
    // Add Google Fonts
    if (!document.getElementById('reset-password-modal-fonts')) {
      const fontLink = document.createElement('link');
      fontLink.id = 'reset-password-modal-fonts';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Open+Sans:wght@400;500;600&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        setError(error.message);
      } else {
        setSuccess('Password updated successfully! You can now sign in with your new password.');
        // Call success callback after a short delay
        setTimeout(() => {
          onSuccess && onSuccess();
        }, 2000);
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 relative font-opensans">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src="/maths_logo.png" alt="MathsCampus Logo" className="h-12 w-auto" />
            <span className="ml-3 text-2xl font-poppins font-bold text-primary">MathsCampus</span>
          </div>
          <h2 className="text-2xl font-poppins font-bold text-primary mb-2">Set New Password</h2>
          <p className="text-gray-600">Enter your new password below</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter new password"
              minLength={6}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Confirm new password"
              minLength={6}
            />
          </div>

          <button
            type="submit"
            disabled={loading || success}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all transform ${
              loading || success
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary hover:bg-blue-800 hover:scale-105'
            }`}
          >
            {loading ? 'Updating...' : success ? 'Password Updated!' : 'Update Password'}
          </button>
        </form>

        {!success && (
          <div className="mt-6 text-center">
            <button
              onClick={onClose}
              className="text-primary hover:text-blue-800 text-sm"
            >
              Cancel
            </button>
          </div>
        )}

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default ResetPasswordPage;